import { Box } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";
import { UI } from "../components/ActionDrawer";
import {
  BackLink,
  Card,
  DefRow,
  Empty,
  Oid,
  Stat,
  Tag,
} from "../components/DashTable";

const rupee = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const ENGAGE_LABELS = {
  starter: "Starter",
  plus: "Plus",
  pro: "Pro",
};

const STATUS_META = {
  generated: { label: "Generated", color: "default", tone: "i" },
  sent_no_response: {
    label: "Sent, no response",
    color: "warning",
    tone: "s",
  },
  accepted: { label: "Accepted", color: "success", tone: "g" },
};

const getStatus = (order) => {
  if (order.quotePdf?.signed) return "accepted";
  if (order.quotePdf?.sent) return "sent_no_response";
  return "generated";
};

/* Text button used for the document actions. */
const linkSx = (muted) => ({
  border: 0,
  bgcolor: "transparent",
  padding: 0,
  color: UI.p,
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "inherit",
  cursor: muted ? "default" : "pointer",
  opacity: muted ? 0.5 : 1,
  "&:hover": muted ? undefined : { textDecoration: "underline" },
});

const QuotationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [order, setOrder] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orderRes, quoteRes] = await Promise.all([
          apiService.getOrder(id, token),
          apiService.downloadQuote(id, token, false).catch(() => null),
        ]);
        setOrder(orderRes.data);
        if (quoteRes?.data?.url) {
          setPdfUrl(quoteRes.data.url);
        }
      } catch (error) {
        toast.error("Failed to load quotation");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await apiService.downloadQuote(id, token, true);
      if (res?.data?.url) {
        const link = document.createElement("a");
        link.href = res.data.url;
        link.download = res.data.fileName || "quote.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error("Download link unavailable");
      }
    } catch (error) {
      toast.error("Failed to download quote");
    } finally {
      setDownloading(false);
    }
  };
  if (loading) {
    return <Loader />;
  }

  const pageSx = {
    padding: { xs: "18px 16px 50px", md: "24px 28px 60px" },
    maxWidth: 1240,
  };

  if (!order) {
    return (
      <Box sx={pageSx}>
        <BackLink onClick={() => navigate("/dashboard/quotations")}>
          ‹ Back to quotations
        </BackLink>
        <Empty title="Quotation not found." />
      </Box>
    );
  }

  const status = getStatus(order);
  const statusMeta = STATUS_META[status];

  return (
    <Box sx={pageSx}>
      <BackLink onClick={() => navigate("/dashboard/quotations")}>
        ‹ Back to quotations
      </BackLink>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "18px",
        }}
      >
        <Box sx={{ fontSize: 22, fontWeight: 500, color: UI.ink }}>
          {order.clinic?.name || "—"}
        </Box>
        <Box
          sx={{
            border: "1px solid",
            borderColor: UI.line,
            bgcolor: UI.bg2,
            borderRadius: "3px",
            padding: "1px 6px",
          }}
        >
          <Oid>{order.zohoEstimateNumber || "—"}</Oid>
        </Box>
        <Tag tone="stage">Quotation</Tag>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "14px",
          bgcolor: UI.surface,
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "8px",
          padding: "15px 16px",
          marginBottom: "22px",
        }}
      >
        <Stat label="Doctor" value={order.doctor?.name} />
        <Stat
          label="City"
          value={
            [order.clinic?.city, order.clinic?.state]
              .filter(Boolean)
              .join(", ") || "—"
          }
        />
        <Stat label="Owner" value={order.ownerId?.name} />
        <Stat label="Value" value={rupee(order.quote?.totalAmount)} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          gap: "18px",
          alignItems: "start",
        }}
      >
        <Card title="Commercials">
          <DefRow label="Product" value={order.quote?.product || "—"} />
          <DefRow
            label="Subscription"
            value={
              order.quote?.engagePlan
                ? `${ENGAGE_LABELS[order.quote.engagePlan] || order.quote.engagePlan}, ${Math.round(
                    (order.quote.tenureMonths || 0) / 12,
                  )} yr`
                : "—"
            }
          />
          {(order.quote?.engageAddOns || []).map((addOn, index) => (
            <DefRow
              key={index}
              label="Subscription (add-on)"
              value={`${ENGAGE_LABELS[addOn.plan] || addOn.plan}, ${Math.round(
                (addOn.tenureMonths || 0) / 12,
              )} yr`}
            />
          ))}
          <DefRow
            label="Payment terms"
            value={order.quote?.paymentTerm || "—"}
          />
          <DefRow label="Order total" value={rupee(order.quote?.totalAmount)} />
          <DefRow
            label="Place of supply"
            value={order.placeOfSupply || "—"}
            last
          />
        </Card>

        <Box sx={{ minWidth: 0 }}>
          <Card title="Documents">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: "6px",
                    bgcolor: UI.bg2,
                    border: "1px solid",
                    borderColor: UI.line,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 17, color: UI.ink3 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: UI.ink,
                      marginBottom: "4px",
                      overflowWrap: "break-word",
                    }}
                  >
                    {order.zohoEstimateNumber || "Quote PDF"}
                  </Box>
                  <Tag tone={statusMeta.tone}>{statusMeta.label}</Tag>
                </Box>
              </Box>
              {pdfUrl ? (
                <Box sx={{ display: "flex", gap: "14px", flexShrink: 0 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => window.open(pdfUrl, "_blank")}
                    sx={linkSx(false)}
                  >
                    Open
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    onClick={downloading ? undefined : handleDownload}
                    sx={linkSx(downloading)}
                  >
                    {downloading ? "Downloading…" : "Download"}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ fontSize: 11, color: UI.ink3 }}>Unavailable</Box>
              )}
            </Box>
          </Card>

          <Card title="Activity">
            {order.timeline?.length ? (
              [...order.timeline]
                .sort((a, b) => new Date(b.at) - new Date(a.at))
                .map((entry, index) => (
                  <Box
                    key={index}
                    sx={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom:
                        index === order.timeline.length - 1
                          ? 0
                          : "1px solid",
                      borderBottomColor: UI.line,
                      "&:last-child": {
                        paddingBottom: 0,
                        marginBottom: 0,
                        borderBottom: 0,
                      },
                    }}
                  >
                    <Box sx={{ fontSize: 13, color: UI.ink }}>{entry.text}</Box>
                    <Box
                      sx={{ fontSize: 11, color: UI.ink3, marginTop: "2px" }}
                    >
                      {entry.by} · {new Date(entry.at).toLocaleString()}
                    </Box>
                  </Box>
                ))
            ) : (
              <Box sx={{ fontSize: 13, color: UI.ink3 }}>No activity yet.</Box>
            )}
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default QuotationDetail;
